import firebase from "firebase/compat/app";
import { observable } from "mobx";
import AccountService from "./AccountService";
import ChatMessengerService from "./ChatMessengerService";

export type ChatContact = {
  contactId: string;
  name: string;
  phone: string;
  email: string;
  date: string;
  lastMessage: string;
};

export type SendStatus =
  | "INVALID_USER"
  | "SUCCESS"
  | "ERROR"
  | "FIRESTORE_ERROR";

export type EmailData = {
  userId: string;
  contactId: string;
  templateId?: string;
  content: string;
  email?: string;
  htmlMessageText?: string;
  sendAt?: Date | null | number;
  dynamicData: any;
};

export default class SendService {
  private _accountService: AccountService;
  private _chatService: ChatMessengerService;
  private _db: firebase.firestore.Firestore = firebase.firestore();
  private _funcs: firebase.functions.Functions = firebase.functions();
  private _sendEmailHandler = this._funcs.httpsCallable("sendEmailHandler");
  private _sendSimpleEmailHandler = this._funcs.httpsCallable(
    "sendSimpleEmailHandler"
  );
  private _sendSMSHandler = this._funcs.httpsCallable("sendSMSHandler");
  private _sendSimpleSMSHandler = this._funcs.httpsCallable(
    "sendSimpleSMSHandler"
  );
  private _sendEmailNotificationHandler = this._funcs.httpsCallable(
    "sendEmailNotification"
  );

  constructor(account: AccountService, chat: ChatMessengerService) {
    this._accountService = account;
    this._chatService = chat;
  }

  async initialize() {
    this._db = firebase.firestore();
  }

  @observable
  private _selectedChatContact: ChatContact | any = null;

  @observable
  private _chatContacts: Array<ChatContact | any> = [];

  public get chatContacts(): Array<ChatContact> {
    return this._chatContacts;
  }

  public get selectedChatContact(): ChatContact {
    return this._selectedChatContact;
  }

  async getChatContacts(): Promise<SendStatus> {
    const user = this._accountService.user;
    if (user == null) {
      return "INVALID_USER";
    }
    try {
      const result = await this._db.collection("chatContacts").get();
      const chatContacts = result.docs.map((doc) => doc.data());
      this._chatContacts = chatContacts;
      if (chatContacts.length > 0) {
        this._selectedChatContact = chatContacts[0];
      }
      return "SUCCESS";
    } catch (err) {
      console.log({ err });
      return "ERROR";
    }
  }

  async sendEmail(data: EmailData): Promise<SendStatus> {
    const { userId, contactId, dynamicData, content } = data;
    try {
      const result = await this._sendEmailHandler(data);
      // save the document in chatMessanger
      if (result.data === "SUCCESS") {
        await this._chatService.saveSentMessages(userId, contactId, {
          mId: dynamicData.mId,
          content,
          status: "sent",
          type: "customer",
        });
        // add contact to chatContacts
        await this._chatService.saveChatContact(
          contactId,
          content,
          dynamicData
        );
        return "SUCCESS";
      } else {
        return "FIRESTORE_ERROR";
      }
    } catch (err) {
      return "ERROR";
    }
  }
  async sendSimpleEmail(data: EmailData): Promise<SendStatus> {
    const { userId, contactId, dynamicData, content } = data;
    try {
      const result = await this._sendSimpleEmailHandler(data);
      // save the document in chatMessanger
      if (result.data === "SUCCESS") {
        await this._chatService.saveSentMessages(userId, contactId, {
          mId: dynamicData.mId,
          content,
          status: "sent",
          type: "customer",
        });
        // add contact to chatContacts
        await this._chatService.saveChatContact(
          contactId,
          content,
          dynamicData
        );
        return "SUCCESS";
      } else {
        return "FIRESTORE_ERROR";
      }
    } catch (err) {
      return "ERROR";
    }
  }

  async sendSMS(data: EmailData): Promise<SendStatus> {
    try {
      const result = await this._sendSMSHandler(data);
      if (result.data === "SUCCESS") {
        // save the document in chatMessanger
        // add contact to chatContacts
      } else {
        return "ERROR";
      }
    } catch (err) {
      console.log({ err });
      return "ERROR";
    }
  }

  async sendSMSFromChatbot(data: any): Promise<SendStatus> {
    try {
      const result = await this._sendSimpleSMSHandler(data);
      if (result.data === "SUCCESS") {
        return "SUCCESS";
      } else {
        return "ERROR";
      }
    } catch (err) {
      return "ERROR";
    }
  }

  async sendEmailNotification(data: any): Promise<SendStatus> {
    try {
      const result = await this._sendEmailNotificationHandler(data);
      return result?.data;
    } catch (err) {
      console.log({
        err,
      });
      return "ERROR";
    }
  }
}
